/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { calendarDateToGnoment, gnoment, gnomentToCalendarDate } from 'shared-ui/gnoment';
import { useLocale } from 'react-aria';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {
    Alert,
    Button,
    DatePicker,
    FauxTextField,
    FlexCol,
    FlexRow,
    Icon,
    NumberField,
    Select,
    Text,
    TextField,
    useFlags,
    VisualOnlyLabel,
} from 'sarsaparilla';

import { DATE_FORMAT_US_SLASHES } from '../constants';
import { FacilityDefaults, FacilityProps, PassTypeProps } from '../props';
import { getSeasonDates } from '../utils';
import { getDuration, validToDate } from '../utils/duration_utils';
import {
    datesBlocked,
    isBeyondBookingWindow,
    isBlackoutDate,
    isBeforeToday,
    isDailyPassType,
    isFreeDay,
} from '../utils/date_time_utils';

const DATE_FORMAT = 'YYYY-MM-DD';

export const getEndDates = (passType, startDateMoment, endDate, seasonEnd) => {
    let isEmptyStartDate = false;
    let validEndDate;
    const typeSegments = passType.features['day-segment'];

    if (typeSegments) {
        validEndDate = gnoment(endDate || undefined);
    }

    if (!typeSegments && (!startDateMoment || !gnoment(startDateMoment).isValid())) {
        validEndDate = gnoment();
    }

    if (!typeSegments && startDateMoment && gnoment(startDateMoment).isValid()) {
        validEndDate = validToDate(startDateMoment, passType);
    }

    let endDateMoment = !validEndDate ? gnoment() : gnoment(validEndDate);

    if (
        passType.features['start-date'] &&
        startDateMoment &&
        gnoment(startDateMoment).isValid()
    ) {
        if (endDateMoment.isBefore(startDateMoment)) {
            endDateMoment = startDateMoment.clone();
        }
    }
    if (['season', 'seasonal'].includes(passType.duration)) {
        validEndDate = gnoment(seasonEnd, DATE_FORMAT);

        if (!validEndDate.isValid()) {
            const [year, month, day] = seasonEnd?.split('-') ?? [];
            const adjustedYear = parseInt(year, 10) + 1;
            validEndDate = gnoment(`${adjustedYear}-${month}-${day}`, DATE_FORMAT);
        }
    }
    if (passType.duration.indexOf('limit-to-season') !== -1) {
        const days = getDuration(passType.duration).days - 1;
        const endValue = gnoment(startDateMoment).add(days, 'd');
        const isAfterSeasonEnd = endValue.isAfter(seasonEnd, 'day');

        validEndDate = endValue;
        endDateMoment = endValue;

        if (isAfterSeasonEnd) {
            validEndDate = gnoment(seasonEnd, DATE_FORMAT);
            endDateMoment = gnoment(seasonEnd, DATE_FORMAT);
        }
    }

    if (
        isDailyPassType(passType?.duration) &&
        (!startDateMoment || isEmpty(startDateMoment))
    ) {
        validEndDate = '';
        endDateMoment = '';
        isEmptyStartDate = true;
    }

    return { validEndDate, endDateMoment, isEmptyStartDate };
};

export default function StartDateForm({
    commercialInfo,
    isScannedPass,
    isInternal,
    endDate = gnoment().format(DATE_FORMAT),
    facility = FacilityDefaults,
    isExistingReservation,
    number = 1,
    onChangeDaySegments,
    onChangeEnd,
    onChangeStart,
    onCommercialInfoChanged,
    passType,
    segments,
    startDate = gnoment().format(DATE_FORMAT),
    dateInfoText,
    renderDateInfoContent: renderDateContent,
    overrideStartDate,
}) {
    const locale = useLocale();

    //Duration of visit select is only for daily pass types
    const passDuration = getDuration(passType?.duration)?.days;

    const durationOptions = Array.from({ length: passDuration }, (_, i) => {
        const x = i + 1;
        return { value: x.toString(), label: `${x} ${x === 1 ? 'day or less' : 'days'}` };
    });

    const generateDates = (
        startDateMoment,
        endDateMoment,
        isInclusionsModified,
        included
    ) => {
        const typeSegments = passType.features['day-segment'];
        if (!typeSegments) return [];

        const dates = [];
        const DATE_FORMAT_UI = 'ddd, ll';
        const date = startDateMoment.clone().startOf('day');
        const end = endDateMoment.clone().add(1, 'day').startOf('day');
        const divideDayInto = typeSegments.segments.length;
        const hourOffset = 24 / divideDayInto;

        do {
            for (let i = 0; i < divideDayInto; i += 1) {
                const segment = typeSegments.segments[i];
                const segEnd = date.clone().add(hourOffset, 'hours');
                const day = date.clone().startOf('day').format(DATE_FORMAT);
                const id = `${day}-${segEnd.format(DATE_FORMAT)}`;

                dates.push({
                    id,
                    day,
                    start: date.format(DATE_FORMAT_UI),
                    end: segEnd.format(DATE_FORMAT_UI),
                    key: segment.key,
                    label: segment.label,
                    included: isInclusionsModified
                        ? get(included, [day, segment.key], true)
                        : true,
                });
                date.add(hourOffset, 'hours');
            }
        } while (date.isBefore(end));

        if (dates.length > 2) dates.pop();
        if (!isInclusionsModified) dates[dates.length - 1].included = false;

        return dates;
    };

    const [isInclusionsModified, setIsInclusionsModified] =
        useState(isExistingReservation);
    const [included, setIncluded] = useState(() => {
        return (
            segments ||
            generateDates(gnoment(startDate), gnoment(endDate), false, {}).reduce(
                (memo, { day, key, included: inc }) => ({
                    ...memo,
                    [day]: { ...memo[day], [key]: inc },
                }),
                {}
            )
        );
    });

    const daySegmentsTimeoutRef = useRef(null);

    useEffect(() => {
        onChangeStart(startDate, true);
        onChangeEnd(endDate, true);
        daySegmentsTimeoutRef.current = setTimeout(
            () => onChangeDaySegments(included),
            250
        );
        return () => clearTimeout(daySegmentsTimeoutRef.current);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const newIncluded = generateDates(
            gnoment(startDate),
            gnoment(endDate),
            isInclusionsModified,
            included
        ).reduce(
            (memo, { day, key, included: inc }) => ({
                ...memo,
                [day]: { ...memo[day], [key]: inc },
            }),
            {}
        );
        setIncluded(newIncluded);
        onChangeDaySegments(newIncluded);
        // eslint-disable-next-line
    }, [startDate, endDate]);

    const isDayBlocked = datesBlocked(facility, passType);

    const getStartDateGnoment = () => {
        if (isDailyPassType(passType.duration)) {
            return !startDate ? '' : gnoment(startDate);
        }
        return !startDate ? gnoment() : gnoment(startDate);
    };

    const onStartDateChanged = (date) => {
        const momentDate =
            date && !gnoment?.isGnoment(date) ? calendarDateToGnoment(date) : date;
        onChangeStart(momentDate?.clone()?.startOf('day')?.format(DATE_FORMAT), true);
    };

    useEffect(() => {
        if (!passType.features['start-date']) return;

        const startDateMoment = getStartDateGnoment();

        if (
            !isEmpty(startDateMoment) &&
            !overrideStartDate &&
            isDayBlocked(startDateMoment)
        ) {
            while (
                isDayBlocked(startDateMoment) &&
                startDateMoment?.isBefore(gnoment().add(1, 'year'))
            ) {
                startDateMoment?.add(1, 'day');
            }
            onStartDateChanged(startDateMoment);
        }
        // eslint-disable-next-line
    }, []);

    const isBefore = (date) => isBeforeToday(date);

    const isAfter = (date) => isBeyondBookingWindow(facility, date);

    const isOutsideRange = (date) => isBefore(date) || isAfter(date);

    const isDateUnavailable = (date) => {
        const momentDate = date ? calendarDateToGnoment(date) : date;
        return datesBlocked(facility, passType)(momentDate) || isOutsideRange(momentDate);
    };

    const getTooltipContent = (day) => {
        const momentDay =
            day && !gnoment?.isGnoment(day) ? calendarDateToGnoment(day) : day;
        const { agency } = facility;

        if (datesBlocked(facility, passType)(momentDay)) {
            if (isFreeDay(facility, momentDay)) {
                return agency === 'NPS'
                    ? 'Fee Free Day for U.S. residents, non-residents pay at park'
                    : 'Fee-Free Day';
            }
            if (isBlackoutDate(facility, momentDay)) {
                const periods = get(facility, 'blackout_periods');
                let textContent = '';
                // eslint-disable-next-line array-callback-return
                periods?.map((item) => {
                    if (
                        momentDay?.isBetween(
                            item.start_date,
                            gnoment.utc(item.end_date).endOf('day').format(),
                            undefined,
                            []
                        )
                    ) {
                        textContent = item.name;
                    }
                });
                return textContent;
            }
            return 'Unavailable';
        }
        return false;
    };

    const getDayType = (day) => {
        const momentDay =
            day && !gnoment?.isGnoment(day) ? calendarDateToGnoment(day) : day;
        if (datesBlocked(facility, passType)(momentDay) || isBefore(momentDay)) {
            return 'blocked';
        }
        if (isAfter(momentDay)) return 'notYetReleased';
        return 'available';
    };

    const getInitialVisibleMonth = () => {
        const initial = gnoment();
        while (isDayBlocked(initial) && initial?.isBefore(gnoment().add(1, 'year'))) {
            initial?.add(1, 'day');
        }
        return initial;
    };

    const addToInclusion = (add, day, key) => {
        setIsInclusionsModified(true);
        setIncluded((prev) => {
            const next = { ...prev, [day]: { ...prev[day], [key]: add } };
            onChangeDaySegments(next);
            return next;
        });
    };

    const onStartDateChangedNew = (date, validation) => {
        if (!date || validation?.isInvalid) {
            onChangeStart('');
            return;
        }
        onChangeStart(date.toString(), true);
    };

    const onEndDateChangedNew = (date, validation) => {
        if (!date || validation?.isInvalid) {
            onChangeEnd('');
            return;
        }
        onChangeEnd(date.toString(), true);
    };

    const commercialFieldsValid = (info) =>
        !!info['bus-number'] &&
        !!info['duration-of-visit'] &&
        info['number-of-passengers'] > 0;

    const onDurationOfVisitChanged = (e) => {
        const value = e?.target?.value ?? e;
        const parsed = parseInt(value, 10);
        const updated = { ...commercialInfo, 'duration-of-visit': parsed };
        onCommercialInfoChanged?.(
            'duration-of-visit',
            parsed,
            commercialFieldsValid(updated)
        );
    };

    const onBusNumberChanged = (e) => {
        const value = e.target.value;
        const updated = { ...commercialInfo, 'bus-number': value };
        onCommercialInfoChanged?.('bus-number', value, commercialFieldsValid(updated));
    };

    const onBusNumberBlur = (e) => {
        const trimmed = e.target.value?.trim();
        if (trimmed !== e.target.value) {
            const updated = { ...commercialInfo, 'bus-number': trimmed };
            onCommercialInfoChanged?.(
                'bus-number',
                trimmed,
                commercialFieldsValid(updated)
            );
        }
    };

    const onNumberOfPassengersChanged = (value) => {
        const updated = { ...commercialInfo, 'number-of-passengers': value };
        onCommercialInfoChanged?.(
            'number-of-passengers',
            value,
            commercialFieldsValid(updated)
        );
    };

    const typeSegments = passType.features['day-segment'];
    const placeholderValue = gnomentToCalendarDate(getInitialVisibleMonth());
    const isCommercialPass = passType?.features?.['is-commercial-pass'];
    const [seasonStart, seasonEnd] = getSeasonDates(passType.periods);
    const labelText = typeSegments ? 'Arrival Date' : 'Start Date';
    const endLabelText = typeSegments ? 'Departure Date' : 'End Date';

    let startDateMoment;
    let startDateInput;

    if (passType.features['start-date']) {
        startDateMoment = getStartDateGnoment();

        const isAnnualExactDate = passType.duration === '12-month';

        startDateInput = (
            <div className="simple-date-picker rec-form-item-wrap">
                <DatePicker
                    id={`start-date-${number}`}
                    label={labelText}
                    isDisabled={
                        isScannedPass ||
                        (isInternal && (!!typeSegments || isAnnualExactDate))
                    }
                    isRequired={isDailyPassType(passType?.duration)}
                    defaultValue={
                        startDateMoment
                            ? gnomentToCalendarDate(startDateMoment)
                            : undefined
                    }
                    placeholderValue={placeholderValue}
                    getDayType={getDayType}
                    getTooltipContent={getTooltipContent}
                    isDateUnavailable={isDateUnavailable}
                    onChange={onStartDateChangedNew}
                />
            </div>
        );
    } else {
        if (['season', 'seasonal'].includes(passType.duration)) {
            startDateMoment = gnoment(seasonStart);
        } else if (isDailyPassType(passType.duration)) {
            startDateMoment = !startDate ? '' : gnoment(startDate);
        } else {
            startDateMoment = !startDate ? gnoment() : gnoment(startDate);
        }

        startDateInput = (
            <TextField
                id={`start-date-${number}`}
                value={startDateMoment?.locale(locale).format('l')}
                label={labelText}
                disabled
            />
        );
    }

    const { validEndDate, endDateMoment, isEmptyStartDate } = getEndDates(
        passType,
        startDateMoment,
        endDate,
        seasonEnd
    );

    const endDateInput = (
        <div className="simple-date-picker rec-form-item-wrap sitepass-end-date">
            <DatePicker
                label={endLabelText}
                defaultValue={
                    endDateMoment ? gnomentToCalendarDate(endDateMoment) : undefined
                }
                getDayType={getDayType}
                getTooltipContent={getTooltipContent}
                isDateUnavailable={isDateUnavailable}
                onChange={onEndDateChangedNew}
                isDisabled={isScannedPass}
            />
        </div>
    );

    const dates = generateDates(
        startDateMoment,
        endDateMoment,
        isInclusionsModified,
        included
    );
    const isSameDayPass =
        dates.length === 2 &&
        startDateMoment
            ?.clone()
            ?.startOf('day')
            ?.isSame(endDateMoment?.clone()?.startOf('day'));
    const hasActions = dates.length >= 1 && !isSameDayPass;

    const passengerObj = passType?.features?.['number-of-passengers'];
    const minNumberOfPassengers = passengerObj?.min ?? 0;
    const maxNumberOfPassengers = passengerObj?.max ?? 9999;

    const getRangeInfo = () => {
        let rangeInfo = '';

        if (passengerObj?.min && passengerObj?.max) {
            rangeInfo = `(min: ${passengerObj.min}, max: ${passengerObj.max})`;
        } else if (passengerObj?.min) {
            rangeInfo = `(min: ${passengerObj.min})`;
        } else if (passengerObj?.max) {
            rangeInfo = `(max: ${passengerObj.max})`;
        }

        return rangeInfo;
    };

    useEffect(() => {
        if (
            isCommercialPass &&
            commercialInfo?.['number-of-passengers'] == null &&
            minNumberOfPassengers > 0
        ) {
            onNumberOfPassengersChanged(minNumberOfPassengers);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCommercialPass]);

    return (
        <div>
            <FlexRow>
                {isCommercialPass && (
                    <FlexCol xl="auto">
                        <div className="rec-form-item-wrap">
                            <VisualOnlyLabel isRequired>
                                Number of Passengers {getRangeInfo()}
                            </VisualOnlyLabel>
                            <Text className="mb-1">
                                For reporting purposes only. Total passengers of any age
                                in the vehicle (not including guides or driver)
                            </Text>
                            <NumberField
                                id={`number-of-passengers-${number}`}
                                units="passengers"
                                isRequired
                                min={minNumberOfPassengers}
                                max={maxNumberOfPassengers}
                                value={
                                    commercialInfo?.['number-of-passengers'] ??
                                    minNumberOfPassengers
                                }
                                onChange={onNumberOfPassengersChanged}
                            />
                        </div>
                    </FlexCol>
                )}
            </FlexRow>
            <FlexRow>
                <FlexCol xl="auto" className="rec-form-item-wrap">
                    {startDateInput}
                </FlexCol>
                {isCommercialPass ? (
                    <FlexCol xl="auto" className="rec-form-item-wrap">
                        <Select
                            id={`duration-of-visit-${number}`}
                            label="Duration of Visit"
                            placeholder="Select duration (days)"
                            options={durationOptions}
                            onChange={onDurationOfVisitChanged}
                            value={
                                commercialInfo?.['duration-of-visit']?.toString() ?? ''
                            }
                            isRequired
                        />
                    </FlexCol>
                ) : (
                    <FlexCol xl="auto">
                        {typeSegments ? (
                            endDateInput
                        ) : (
                            <FauxTextField
                                id={`through-date-${number}`}
                                className="rec-form-item-wrap"
                                value={
                                    !isEmptyStartDate
                                        ? gnoment(validEndDate).locale(locale).format('l')
                                        : 'Date'
                                }
                                label="Valid Through"
                                isDisabled
                            />
                        )}
                    </FlexCol>
                )}
            </FlexRow>
            {isCommercialPass && (
                <FlexRow>
                    <FlexCol xl="auto" className="rec-form-item-wrap">
                        <TextField
                            label="Vehicle Identifier (bus number or vehicle make, 10 characters max)"
                            id={`bus-number-${number}`}
                            onChange={onBusNumberChanged}
                            onBlur={onBusNumberBlur}
                            value={commercialInfo?.['bus-number'] ?? ''}
                        />
                    </FlexCol>
                </FlexRow>
            )}
            {(renderDateContent || dateInfoText) && (
                <Alert className="mb-2" type="info">
                    {renderDateContent?.() || dateInfoText}
                </Alert>
            )}
            {get(passType, ['features', 'day-segment']) && !isInternal && (
                <table className="parkpass-pass-day-segment-table">
                    <caption className="rec-sr-only">Pass type and date</caption>
                    <thead>
                        <tr>
                            <th>Pass Type</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            {hasActions && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {dates.map(
                            (
                                { id, day, start, end, key, label, included: inc },
                                index
                            ) => {
                                if (isSameDayPass && index === 1) return null;
                                return (
                                    <tr
                                        key={id}
                                        className={
                                            inc ? '' : 'parkpass-day-segment-excluded'
                                        }
                                    >
                                        <td
                                            className={`parkpass-day-segment parkpass-day-segment-${key}`}
                                        >
                                            {label}
                                        </td>
                                        <td>{start}</td>
                                        <td>{end}</td>
                                        {hasActions && (
                                            <td>
                                                <span>
                                                    {inc ? (
                                                        <Button
                                                            appearance="link"
                                                            gaTrackingId="393714778882"
                                                            onClick={() =>
                                                                addToInclusion(
                                                                    false,
                                                                    day,
                                                                    key
                                                                )
                                                            }
                                                            aria-label="Exclude Pass"
                                                            iconBeforeElement={
                                                                <Icon
                                                                    iconName="remove-circle"
                                                                    title="Exclude Pass"
                                                                />
                                                            }
                                                        >
                                                            Exclude
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            appearance="link"
                                                            gaTrackingId="755075802251"
                                                            onClick={() =>
                                                                addToInclusion(
                                                                    true,
                                                                    day,
                                                                    key
                                                                )
                                                            }
                                                            aria-label="Include Pass"
                                                            iconBeforeElement={
                                                                <Icon
                                                                    iconName="add-circle"
                                                                    title="Include Pass"
                                                                />
                                                            }
                                                        >
                                                            Include
                                                        </Button>
                                                    )}
                                                </span>
                                            </td>
                                        )}
                                    </tr>
                                );
                            }
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}

StartDateForm.propTypes = {
    commercialInfo: PropTypes.shape({
        'duration-of-visit': PropTypes.number,
        'bus-number': PropTypes.string,
        'number-of-passengers': PropTypes.number,
    }),
    isScannedPass: PropTypes.bool,
    isInternal: PropTypes.bool,
    endDate: PropTypes.string,
    facility: FacilityProps,
    isExistingReservation: PropTypes.bool,
    number: PropTypes.number,
    onChangeDaySegments: PropTypes.func,
    onChangeEnd: PropTypes.func,
    onChangeStart: PropTypes.func,
    onCommercialInfoChanged: PropTypes.func,
    passType: PassTypeProps,
    segments: PropTypes.object,
    startDate: PropTypes.string,
    dateInfoText: PropTypes.string,
    renderDateInfoContent: PropTypes.func,
    overrideStartDate: PropTypes.bool,
};

// cspell:ignore parkpass, sitepass
